import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, FieldRow, KeepTogether, PageBreak, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, COTENANT_SUFFIX } from '../../../parties/index.js';
export const metadata = {
    // Document sections in fixed order (per ADR-0049). This template's
    // editor field-groups do NOT map 1:1 to numbered document sections,
    // so field.section is left as editor labels (no § shown in the form);
    // numbering + anchors come purely from these refs + the Body.
    sections: [
        { ref: 'meddelande', title: 'Meddelande' },
        { ref: 'underskrift-av-meddelandet', title: 'Underskrift av meddelandet' },
        { ref: 'andrade-hyresvillkor', title: 'Ändrade hyresvillkor' },
        { ref: 'underskrift-av-tillagget', title: 'Underskrift av tillägget' },
    ],
    templateKey: 'andring_hyresvillkor_forhandlingsklausul',
    version: 'v1',
    category: 'Ändring av hyresvillkor',
    title: 'Meddelande om ändring – införande av förhandlingsklausul',
    shortTitle: 'Ändring — införande av förhandlingsklausul',
    titleSuffix: { key: 'contract_number', prefix: 'avtal' },
    formNumber: 'Formulär R-12',
    preamble: 'Hyresvärden meddelar härmed hyresgästen om föreslagen ändring av hyresvillkoren genom införande av förhandlingsklausul.',
    description: 'Enligt 12 kap. 54 § jordabalken.\n\n' +
        'Specialvariant av "Meddelande om ändring av hyresvillkor" där det som föreslås är att en förhandlingsklausul ska införas i hyresavtalet. Lagtexten i tillägget är förtryckt — använd den generiska mallen "Ändring av hyresvillkor" om andra villkor ska ändras.\n\n' +
        'Vem undertecknar: hyresvärden (meddelandet) och hyresgästen — samt eventuell medhyresgäst — (tillägget).\n' +
        'Leverans: skickas via Scrive.\n\n' +
        'Om hyresgästen inte skriver under tillägget inom svarsfristen får hyresvärden lämna in ärendet till hyresnämnden, dock tidigast en månad efter att meddelandet lämnats.\n\n' +
        'Kopplad mall — skapas under det bostadshyresavtal som ändringen avser.',
    status: 'active',
    // X-6 (André 2026-05-30): statutory "meddelande enligt lag" — printed &
    // posted, never Scrive. Paper-only.
    signingMode: 'manual_delivery',
    connectedTo: ['bostadshyra'],
    // Same lease, same parties + lägenhet (ADR-0055). Roles line up 1:1 with
    // the parent (lessor0 = hyresvärd, lessee0/1 = hyresgäst), so
    // by-role-ordinal is correct. `contract_number` carries the original
    // avtalsnummer the notice refers to — see IMPORT_TODO in
    // docs/architecture/connected-import-todos.md: it has no extraction path,
    // so under an *imported* parent it inherits empty.
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [
            { key: 'apartment_official_number', fromParentKey: 'property_official_apt_no' },
            { key: 'contract_number', fromParentKey: 'contract_number' },
        ],
    },
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            signingLabel: 'Hyresvärden (firmatecknare/ombud)',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst (primär)',
            signingLabel: 'Hyresgästen',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            signingLabel: 'Ev. hyresgäst 2',
            role: 'lessee',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om kontraktet har två hyresgäster.',
        },
    ],
    // Party projection (ADR-0065) — editor fields; body is a custom summary.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'Hyresvärd',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
                { attr: 'address', key: 'landlord_address' },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'Hyresgäst',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'address', key: 'tenant_address' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'Medhyresgäst',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'address', key: 'cotenant_address' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Parter — generated from metadata.partyProjection (ADR-0065). The body
    // renders a selective party summary under role labels.
    ...expandPartyProjection(metadata),
    // Lägenheten / fastigheten
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'Lägenheten',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'Lägenheten',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_address',
        label: 'Postadress',
        type: 'textarea',
        section: 'Lägenheten',
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'apartment_official_number',
        label: 'Lägenhetsnummer (officiell)',
        type: 'text',
        section: 'Lägenheten',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'apartment_rooms',
        label: 'Antal rum och kök',
        type: 'select',
        section: 'Lägenheten',
        requiredAtFinal: true,
        options: [
            { value: '1 rum och kokskåp', label: '1 rum och kokskåp', code: 'r1_kokskap', summary: '1 room with a kitchenette cupboard.' },
            { value: '1 rum och kokvrå', label: '1 rum och kokvrå', code: 'r1_kokvra', summary: '1 room with a kitchen nook.' },
            { value: '1 rum och kök', label: '1 rum och kök', code: 'r1_kok', summary: '1 room with a full kitchen.' },
            { value: '1,5 rum och kök', label: '1,5 rum och kök', code: 'r1_5_kok', summary: '1.5 rooms with a full kitchen.' },
            { value: '2 rum och kokvrå', label: '2 rum och kokvrå', code: 'r2_kokvra', summary: '2 rooms with a kitchen nook.' },
            { value: '2 rum och kök', label: '2 rum och kök', code: 'r2_kok', summary: '2 rooms with a full kitchen.' },
            { value: '2,5 rum och kök', label: '2,5 rum och kök', code: 'r2_5_kok', summary: '2.5 rooms with a full kitchen.' },
            { value: '3 rum och kök', label: '3 rum och kök', code: 'r3_kok', summary: '3 rooms with a full kitchen.' },
            { value: '3,5 rum och kök', label: '3,5 rum och kök', code: 'r3_5_kok', summary: '3.5 rooms with a full kitchen.' },
            { value: '4 rum och kök', label: '4 rum och kök', code: 'r4_kok', summary: '4 rooms with a full kitchen.' },
            { value: '4,5 rum och kök', label: '4,5 rum och kök', code: 'r4_5_kok', summary: '4.5 rooms with a full kitchen.' },
            { value: '5 rum och kök', label: '5 rum och kök', code: 'r5_kok', summary: '5 rooms with a full kitchen.' },
            { value: '6 rum och kök', label: '6 rum och kök', code: 'r6_kok', summary: '6 rooms with a full kitchen.' },
            { value: '7 rum och kök', label: '7 rum och kök', code: 'r7_kok', summary: '7 rooms with a full kitchen.' },
            { value: '8 rum och kök', label: '8 rum och kök', code: 'r8_kok', summary: '8 rooms with a full kitchen.' },
        ],
    },
    // Hyresavtalet
    {
        key: 'contract_number',
        label: 'Hyreskontrakt nr',
        type: 'text',
        section: 'Hyresavtalet',
        requiredAtFinal: true,
        help: 'Numret på det bostadshyresavtal som ändringen avser.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Ändring
    {
        key: 'effective_date',
        label: 'Förhandlingsklausulen införs fr.o.m.',
        type: 'date',
        section: 'Ändring',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Svarsfrist
    {
        key: 'response_deadline',
        label: 'Svarsfrist',
        type: 'date',
        section: 'Svarsfrist',
        requiredAtFinal: true,
        help: 'Datum då ett undertecknat exemplar av tillägget senast önskas åter.',
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Underskrift
    {
        key: 'signing_city',
        label: 'Ort',
        type: 'text',
        section: 'Underskrift',
        requiredAtFinal: true,
        help: 'T.ex. Stockholm',
    },
    {
        key: 'signing_date',
        label: 'Datum',
        type: 'date',
        section: 'Underskrift',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'landlord_signatory_name',
        label: 'Firmatecknare / ombud (namnförtydligande)',
        type: 'text',
        section: 'Underskrift',
        help: 'Namn på den person som undertecknar för hyresvärdens räkning.',
    },
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556677-8899',
    landlord_address: 'Sveavägen 100\n113 50 Stockholm',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    tenant_address: 'Götgatan 35\n116 21 Stockholm',
    cotenant_name: 'Bo Andersson',
    cotenant_personnummer: '19830605-2345',
    cotenant_address: 'Götgatan 35\n116 21 Stockholm',
    property_designation: 'Klockaren 5',
    property_municipality: 'Stockholm',
    property_address: 'Götgatan 35\n116 21 Stockholm',
    apartment_official_number: '1101',
    apartment_rooms: '2 rum och kök',
    contract_number: 'A-2042',
    effective_date: '2026-09-01',
    response_deadline: '2026-07-15',
    signing_city: 'Stockholm',
    signing_date: '2026-06-15',
    landlord_signatory_name: 'Carl Carlsson',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
function Checkbox({ checked }) {
    return (_jsx("span", { className: "mr-1 inline-block h-3 w-3 border border-stone-500 text-center align-middle leading-3", "aria-hidden": true, children: checked ? '✓' : '' }));
}
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
function InfoTable({ values, }) {
    return (_jsxs("div", { className: "my-3 border border-stone-300", children: [_jsx(FieldRow, { label: "Hyresv\u00E4rd", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Hyreskontrakt nr", children: v(values.contract_number) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Hyresg\u00E4st", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.tenant_personnummer) }), _jsx(FieldRow, { label: "Adress", children: v(values.tenant_address) }), _jsx(FieldRow, { label: "Postadress", children: v(values.property_address) }), _jsx(FieldRow, { label: "L\u00E4genhetsnummer (officiell)", children: v(values.apartment_official_number) })] }));
}
// ─── Document 1 — Meddelandet (signed by the hyresvärd) ──────────────────
// MF-1 (André 2026-05-30): the tillägg page is the förhandlingsklausul
// special case — its chapter restarts its own numbering (§1 Ändrade
// hyresvillkor / §2 Underskrift), so each page gets its own provider rather
// than continuing the other page's §-count (per ADR-0049 / ADR-0057).
export function MeddelandeBody({ values, mode, signatories, }) {
    const page1Sections = (metadata.sections ?? []).filter((s) => s.ref === 'meddelande' || s.ref === 'underskrift-av-meddelandet');
    return (_jsxs(SectionNumberingProvider, { sections: page1Sections, values: values, mode: mode, customSections: false, children: [_jsx("p", { className: "mb-1 text-[12px] font-semibold uppercase tracking-wide", children: "Bostadsl\u00E4genhet" }), _jsx("p", { className: "mb-3 text-[12.5px] font-semibold", children: "Meddelande om \u00E4ndring av hyresvillkor: inf\u00F6rande av f\u00F6rhandlingsklausul enligt 12 kap. 54 \u00A7 jordabalken" }), _jsx(InfoTable, { values: values }), _jsxs(Section, { sectionRef: "meddelande", title: "Meddelande", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: ["H\u00E4rmed meddelas att undertecknad hyresv\u00E4rd fr.o.m.", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.effective_date) }), ' ', "\u00F6nskar \u00E4ndra hyresvillkoren f\u00F6r din/er bostadsl\u00E4genhet om", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.apartment_rooms) }), ' ', "i fastigheten", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.property_designation) }), ' ', "i", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.property_municipality) }), ' ', "kommun."] }), _jsx("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: "De \u00E4ndrade villkoren avser inf\u00F6rande av f\u00F6rhandlingsklausul." }), _jsx("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: "F\u00F6rhandlingsklausulen f\u00F6resl\u00E5s \u00E4ndras enligt f\u00F6ljande:" }), _jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: true }), "Bifogade till\u00E4gg till hyresavtalet (i \u00F6vrigt of\u00F6r\u00E4ndrade villkor)"] }), _jsxs("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: ["Ett exemplar av till\u00E4gg till hyresavtalet \u00F6nskas \u00E5ter i undertecknat skick senast den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.response_deadline) }), ". Om \u00F6verenskommelse om de nya villkoren inte kan tr\u00E4ffas, kommer ans\u00F6kan om \u00E4ndring av hyresvillkoren att l\u00E4mnas in till hyresn\u00E4mnden en m\u00E5nad efter det att detta meddelande l\u00E4mnats till dig/er."] })] }), _jsx(KeepTogether, { children: _jsxs(Section, { sectionRef: "underskrift-av-meddelandet", title: "Underskrift av meddelandet", children: [_jsxs("p", { className: "mt-1 mb-3 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "font-medium", children: "Ort och datum:" }), ' ', _jsxs("span", { className: "border-b border-stone-300 font-serif", children: [v(values.signing_city), ", ", v(values.signing_date)] })] }), _jsx(FieldRow, { label: "Hyresv\u00E4rdens namn", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Namnf\u00F6rtydligande", children: v(values.landlord_signatory_name) }), _jsx("div", { className: "mt-3", children: _jsx(SigningBlock, { parties: [
                                    { label: 'Hyresvärden (firmatecknare/ombud)', role: 'lessor', ordinal: 0 },
                                ], signatories: signatories, hideHeading: true }) })] }) })] }));
}
// ─── Document 2 — Tillägget (signed by the hyresgäst[er] + hyresvärd) ─────
export function TillaggBody({ values, mode, signatories, }) {
    const showCotenant = cond(mode, !!values.cotenant_name);
    const page2Sections = (metadata.sections ?? []).filter((s) => s.ref === 'andrade-hyresvillkor' || s.ref === 'underskrift-av-tillagget');
    return (_jsxs(SectionNumberingProvider, { sections: page2Sections, values: values, mode: mode, customSections: false, children: [_jsx("p", { className: "mb-1 text-[12px] font-semibold uppercase tracking-wide", children: "Till\u00E4gg till hyresavtal" }), _jsx(InfoTable, { values: values }), _jsxs("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: ["Undertecknade parter har denna dag tr\u00E4ffat f\u00F6ljande till\u00E4gg till hyreskontrakt nr", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.contract_number) }), "."] }), _jsxs(Section, { sectionRef: "andrade-hyresvillkor", title: "\u00C4ndrade hyresvillkor", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: true }), "Bifogade till\u00E4gg till hyresavtalet (i \u00F6vrigt of\u00F6r\u00E4ndrade villkor)"] }), _jsx("p", { className: "mt-2 text-[10.5px] italic leading-relaxed", children: "\u201DF\u00F6rhandlingsklausul g\u00E4ller f\u00F6r hyresavtalet, vilket inneb\u00E4r att hyran f\u00F6r l\u00E4genheten f\u00F6rhandlas fram med den hyresg\u00E4storganisation som \u00E4r part i den f\u00F6r huset g\u00E4llande f\u00F6rhandlingsordningen.\u201D" }), _jsxs("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: ["Hyresvillkoren g\u00E4ller fr.o.m.", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.effective_date) }), "."] }), _jsx("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: "Hyresavtalet f\u00F6resl\u00E5s forts\u00E4tta g\u00E4lla i \u00F6vrigt p\u00E5 of\u00F6r\u00E4ndrade villkor." })] }), _jsx(KeepTogether, { children: _jsx(Section, { sectionRef: "underskrift-av-tillagget", title: "Underskrift av till\u00E4gget", children: _jsx(SigningBlock, { parties: showCotenant
                            ? [
                                { label: 'Hyresgästen', role: 'lessee', ordinal: 0 },
                                { label: 'Ev. hyresgäst 2', role: 'lessee', ordinal: 1 },
                                { label: 'Hyresvärdens representant', role: 'lessor', ordinal: 0 },
                            ]
                            : [
                                { label: 'Hyresgästen', role: 'lessee', ordinal: 0 },
                                { label: 'Hyresvärdens representant', role: 'lessor', ordinal: 0 },
                            ], signatories: signatories, hideHeading: true }) }) })] }));
}
/**
 * Two physically separate documents (per ADR-0057): the meddelande (page
 * 1, hyresvärd signs) and the tillägg (page 2, hyresgäst[er] sign). Sibling
 * of `andring_hyresvillkor` with förtryckt förhandlingsklausul-lagtext.
 * Manual/paper-signed (statutory meddelande); Scrive unsupported.
 */
export const documents = [
    { key: 'meddelande', title: 'Meddelande om ändring av hyresvillkor', Body: MeddelandeBody },
    { key: 'tillagg', title: 'Tillägg till hyresavtalet', Body: TillaggBody },
];
// Back-compat single-document body: composes both parts with a page break
// between them (per ADR-0057).
export function Body(props) {
    return (_jsxs(_Fragment, { children: [_jsx(MeddelandeBody, { ...props }), _jsx(PageBreak, {}), _jsx(TillaggBody, { ...props })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map